import React, { useEffect } from "react"
import { Box } from "@chakra-ui/react"
import Script from "next/script"
import { withErrorBoundary } from "react-error-boundary"
import { useInView } from "react-intersection-observer"

import { Logger } from "lib/logger"
import { trackProductReviewViewed } from "utils/gtm"
import { useLocale } from "hooks"

import { useSegment } from "tracking"

import styles from "./reviews.legacy.module.css"
import { colours } from "@shared/ui.design-system"

const isProduction = ["production", "test"].includes(
  process.env.NODE_ENV as string
)
const localeMap = { "en-GB": "en_GB", "en-US": "en_US" }

const ReviewsComponent: React.FC<{ children?: React.ReactNode }> = ({
  children,
  ...props
}) => {
  const locale = useLocale()
  const lang = localeMap[locale]

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  })

  const { track } = useSegment()

  useEffect(() => {
    if (inView) {
      trackProductReviewViewed()

      track({
        event: "Page Element Viewed",
        properties: {
          module_content: "PDP Reviews",
        },
      })
    }
  }, [track, inView])

  return (
    <Box
      id="reviews"
      width="100%"
      bgColor={colours.accent.light}
      mt={[3, 3, 8, 9]}
      scrollMarginTop={{ base: "110px", lg: "150px" }}
      className={styles.reviews}
    >
      {isProduction && (
        <Script
          strategy="lazyOnload"
          type="text/javascript"
          src={`https://apps.bazaarvoice.com/deployments/beautypie/main_site/${process.env.NEXT_PUBLIC_BV_ENVIRONMENT}/${lang}/bv.js`}
        />
      )}
      <Box
        w="100%"
        maxW="90rem"
        px={{ base: 1, sm: 0, lg: "7.5rem" }}
        py={{ lg: "5px" }}
        mx="auto"
      >
        <Box
          ref={ref}
          mt={[3, 3, 2, 2]}
          data-testid="product-reviews-grid"
          id="product-reviews-grid"
          scrollMarginTop="40px"
          flexDirection="column"
          {...props}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export const Reviews = withErrorBoundary(ReviewsComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`Reviews failed to load. ${error}`)
  },
})
